import React, { FC, useMemo, useState } from "react";
import { useFormikContext } from "formik";
import FormikTextField from "../../Unknown/FormikTextField";
import Grid from "../../Unknown/Grid";
import MenuItem from "../../Unknown/MenuItem";
import useTranslations from "./useTranslations";
import {
  CenterPackageFormValues,
  LabelTranslationsKey,
  Offering,
} from "./types";
import { inputSpacing, visibilityTypes } from "./constants";
import InputAdornment from "../../Unknown/InputAdornment";
import Typography from "../../Unknown/Typography";
import CenterPackageOfferingSelectField from "../CenterPackageOfferingSelectField";
import useStyles from "./useStyles";
import { OfferingOption } from "../CenterPackageOfferingSelectField/types";
import getOfferingsPrice from "./getOfferingsPrice";
import FormikRichTextField from "../../Unknown/FormikRichTextField";
import getOfferingsDurationMinutes from "./getOfferingsDurationMinutes";

type CenterPackageFormProps = {
  currencyCode: string;
  labelTranslations: Record<LabelTranslationsKey, string>;
  offerings: Offering[];
};

const CenterPackageForm: FC<CenterPackageFormProps> = ({
  currencyCode,
  labelTranslations,
  offerings,
}) => {
  const { priceHelperRoot } = useStyles();

  const { isSubmitting, values } = useFormikContext<CenterPackageFormValues>();

  const [offeringsPrice, setOfferingsPrice] = useState(
    getOfferingsPrice({ offerings, selectedOptions: values.offerings }),
  );

  const [offeringsDuration, setOfferingsDuration] = useState(
    getOfferingsDurationMinutes({
      offerings,
      selectedOptions: values.offerings,
    }),
  );

  const translations = useTranslations({
    currencyCode,
    offeringsPrice,
    durationMinutes: offeringsDuration,
  });

  const onOfferingsChange = (newValue: OfferingOption[]) => {
    const newOfferingsPrice = getOfferingsPrice({
      offerings,
      selectedOptions: newValue,
    });

    setOfferingsPrice(newOfferingsPrice);

    const newOfferingsDuration = getOfferingsDurationMinutes({
      offerings,
      selectedOptions: newValue,
    });

    setOfferingsDuration(newOfferingsDuration);
  };

  const offeringsOptions = useMemo(() => {
    return offerings.map(
      ({ id, name, visibility, durationMinutes, price }) => ({
        id,
        name,
        durationMinutes,
        visibility,
        price,
      }),
    );
  }, [offerings]);

  return (
    <Grid container spacing={inputSpacing} direction="row">
      <Grid item container spacing={inputSpacing}>
        <Grid item xs={4}>
          <FormikTextField
            fullWidth
            required
            disabled={isSubmitting}
            name="name"
            label={labelTranslations.labelName}
          />
        </Grid>
        <Grid item xs={4}>
          <FormikTextField
            fullWidth
            required
            disabled={isSubmitting}
            name="modalityName"
            label={labelTranslations.labelModalityName}
          />
        </Grid>
        <Grid item xs={4}>
          <CenterPackageOfferingSelectField
            offerings={offeringsOptions}
            onChange={onOfferingsChange}
            textFieldProps={{
              label: labelTranslations.labelOfferings,
              name: "offerings",
              required: true,
              disabled: isSubmitting,
              fullWidth: true,
            }}
          />
        </Grid>
      </Grid>
      <Grid item container spacing={inputSpacing}>
        <Grid item xs={4}>
          <Typography variant="body1">{translations.textPrice}</Typography>
          <Typography className={priceHelperRoot}>
            {translations.helperPrice}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <FormikTextField
            fullWidth
            disabled={isSubmitting}
            type="number"
            name="price"
            label={labelTranslations.labelPrice}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography>{translations.currencySymbol}</Typography>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <FormikTextField
            fullWidth
            required
            disabled={isSubmitting}
            type="number"
            name="depositAmount"
            label={labelTranslations.labelDepositAmount}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography>{translations.currencySymbol}</Typography>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item container spacing={inputSpacing}>
          <Grid item xs={4}>
            <Typography variant="body1">{translations.textDuration}</Typography>
            <Typography className={priceHelperRoot}>
              {translations.helperDuration}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <FormikTextField
              fullWidth
              disabled={isSubmitting}
              type="number"
              name="durationMinutes"
              label={labelTranslations.labelDuration}
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">
                    <Typography>{translations.minutes}</Typography>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <FormikTextField
              fullWidth
              required
              disabled={isSubmitting}
              name="visibility"
              label={labelTranslations.labelVisibility}
              select
            >
              {visibilityTypes.map((visibilityType) => (
                <MenuItem key={visibilityType.id} value={visibilityType.id}>
                  {translations[visibilityType.translationKey]}
                </MenuItem>
              ))}
            </FormikTextField>
          </Grid>
        </Grid>
      </Grid>
      <Grid item container spacing={inputSpacing}>
        <Grid item xs={12}>
          <FormikTextField
            fullWidth
            required
            disabled={isSubmitting}
            name="description"
            label={labelTranslations.labelDescription}
            multiline
            maxRows={4}
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <FormikRichTextField
          name="marketingText"
          label={labelTranslations.labelMarketingText}
          placeholder={labelTranslations.marketingPlaceholder}
          required
        />
      </Grid>
    </Grid>
  );
};

export default CenterPackageForm;
